import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import consumerApi, { AUTH_SHAPE } from '../../../service/consumerApi';
import { somethingWrongNotification } from '../../notifier/notifierActions';
import { clearConsumerSession, setConsumerSession } from '../consumerReducer';
import {
  LANGUAGES_SHORT,
  PROJECT_TYPES,
  RESONANCE_STRUCTURE,
  RESONANCE_THEMES,
  RESONANCE_VISIONS,
} from '../../../constants';

const GET_RESONANCE_PROJECT_INFO = 'consumer/resonance/GET_RESONANCE_DATA';
const GET_RESONANCE_INIT_DATA = 'consumer/resonance/GET_RESONANCE_INIT_DATA';
const GET_FILTERED_MESSAGES = 'consumer/resonance/GET_FILTERED_MESSAGES';
const GET_SINGLE_MESSAGE_CORRELATIONS = 'consumer/resonance/GET_SINGLE_MESSAGE_CORRELATIONS';
const GET_RESONANCE_CONCEPTS = 'consumer/resonance/GET_RESONANCE_CONCEPTS';
const GET_RESONANCE_H2H_CONCEPTS = 'consumer/resonance/GET_RESONANCE_H2H_CONCEPTS';

export const getConsumerResonanceData = createAsyncThunk(
  GET_RESONANCE_PROJECT_INFO,
  async ({ projectName, projectPassword }, { dispatch, getState, rejectWithValue }) => {
    const serverUrl = getState().securityNew.serverUrl;

    if (!serverUrl) {
      dispatch(somethingWrongNotification());
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/data`, null, {
          authShape: AUTH_SHAPE.FLAT,
          consumerCredentials: { projectName, projectPassword },
        }),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          dispatch(clearConsumerSession());
          return rejectWithValue(null);
        },
      },
    );

    if (response.data?.dataStructure === RESONANCE_STRUCTURE.MESSAGES) {
      const query = [
        `projectName=${projectName}`,
        `projectType=${PROJECT_TYPES.RESONANCE}`,
        `projectPassword=${projectPassword}`,
        `theme=${response.data.theme?.toLowerCase() || RESONANCE_THEMES.FACTIVE}`,
        `vision=${response.data.vision?.toLowerCase() || RESONANCE_VISIONS.DEFAULT}`,
        `dataStructure=${response.data.dataStructure?.toLowerCase()}`,
        `lang=${response.data.lang?.toLowerCase() || LANGUAGES_SHORT.ENGLISH}`,
      ];

      window.open(`${serverUrl}/chart/resonance/?${query.join('&')}`, '_blank').focus();
      dispatch(clearConsumerSession());
      return rejectWithValue(null);
    }

    dispatch(
      setConsumerSession({
        projectType: PROJECT_TYPES.RESONANCE,
        projectName,
        projectPassword,
      }),
    );

    return { projectName, ...response.data };
  },
);

export const getConsumerResonanceInitData = createAsyncThunk(
  GET_RESONANCE_INIT_DATA,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/resonance/calculations/init/data`, null, {
          authShape: AUTH_SHAPE.FLAT,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

export const getConsumerFilteredMessages = createAsyncThunk(
  GET_FILTERED_MESSAGES,
  async ({ appliedFilters }, { dispatch, rejectWithValue }) => {
    if (!appliedFilters.length) {
      return rejectWithValue(null);
    }

    const audienceIds = appliedFilters.map((filter) => filter.id);

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/resonance/calculations/filter/messages`, audienceIds, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

export const getConsumerSingleMessageCorrelations = createAsyncThunk(
  GET_SINGLE_MESSAGE_CORRELATIONS,
  async ({ messageId }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/resonance/calculations/high/performing`, messageId, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

export const getConsumerResonanceConcepts = createAsyncThunk(
  GET_RESONANCE_CONCEPTS,
  async ({ audiences }, { dispatch, rejectWithValue }) => {
    const audienceIds = audiences.map((audience) => audience.id);

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/resonance/calculations/concepts`, audienceIds, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const { averages, concepts, respondents } = response.data;

    return { averages, concepts, respondents };
  },
);

export const getConsumerResonanceH2HConcepts = createAsyncThunk(
  GET_RESONANCE_H2H_CONCEPTS,
  async ({ audiences }, { dispatch, rejectWithValue }) => {
    const audienceIds = audiences.map((audience) => audience.id);

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/resonance/calculations/h2h/concepts`, audienceIds, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

const defaultErrorHandler = (e, dispatch, rejectWithValue) => {
  dispatch(somethingWrongNotification());
  return rejectWithValue(e);
};
